import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, BRAND, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { toast } from "sonner";
import { Users } from "lucide-react";

export default function MyClass() {
  const { user } = useAuth();
  const [student, setStudent] = useState(null);
  const [klass, setKlass] = useState(null);
  const [classmates, setClassmates] = useState([]);

  useEffect(() => {
    if (!user?.student_id) return;
    (async () => {
      try {
        const { data: s } = await api.get(`/students/${user.student_id}`);
        setStudent(s);
        if (s.class_id) {
          const [cRes, sRes] = await Promise.all([
            api.get("/classes"),
            api.get(`/classes/${s.class_id}/students`),
          ]);
          const c = cRes.data.find(x => x.id === s.class_id);
          setKlass(c);
          setClassmates(sRes.data);
        }
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, [user]);

  return (
    <div data-testid="student-class-page">
      <PageHeader eyebrow="Treino" title="Minha turma" mascot={BRAND.DUO_ACAO} />
      {!student?.class_id ? (
        <EmptyState title="Você ainda não está em uma turma" description="Assim que for alocado, os detalhes aparecerão aqui." mascot={BRAND.MANGUITO} />
      ) : !klass ? (
        <div className="text-muted-foreground">Carregando...</div>
      ) : (
        <>
          <div className="vv-card p-6 mb-6">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--vv-lime))]">{klass.category}</div>
            <h2 className="text-3xl font-black">{klass.name}</h2>
            <div className="mt-3 space-y-1.5">
              {(Array.isArray(klass.schedule) && klass.schedule.length
                ? klass.schedule
                : (klass.days || []).map(d => {
                    const [s, e] = String(klass.time || "").split("-").map(x => x?.trim());
                    return { day: d, start: s, end: e };
                  })
              ).map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className="vv-chip vv-chip-blue min-w-[42px] justify-center">{s.day}</span>
                  <span className="font-bold text-[hsl(var(--vv-yellow))]">{s.start || "—"}</span>
                  <span className="text-muted-foreground">às</span>
                  <span className="font-bold text-[hsl(var(--vv-yellow))]">{s.end || "—"}</span>
                </div>
              ))}
            </div>
            {klass.description && <p className="mt-3 text-muted-foreground">{klass.description}</p>}
          </div>

          <div className="vv-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Users className="size-5 text-[hsl(var(--vv-yellow))]" />
              <h3 className="text-xl font-black">Colegas de turma</h3>
              <span className="vv-chip vv-chip-lime ml-auto">{classmates.length}</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {classmates.map((c) => (
                <div key={c.id} className="p-3 rounded border border-border bg-muted/30">
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.position || "Sem posição"} • {c.category}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
